import React, { useState } from "react";

import { Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import { RiShareLine } from "react-icons/ri";

import { useAppState } from "../../../../../../../../context/StateContext";
import { Customer } from "../../../../../../../../models/customer";
import { Mail } from "../../../../../../../../models/mail";
import MailService from "../../../../../../../../services/mail.service";
import Button, { Color } from "../../../../../../../Form/Button";
import Input from "../../../../../../../Form/Input";
import { NotificationToast } from "../../../../../../../Generic/Toast/NotificationToast";
import useMessages from "../../../../../../hooks/useMessages/useMessages";
import useVideoContext from "../../../../../../hooks/useVideoContext/useVideoContext";
import useStyles from "./styles";

interface Props {
  customer?: Customer;
  taskId?: number;
  estimateShared: boolean;
  setEstimateShared: (state: boolean) => void;
}

enum ShareType {
  APP = "app",
  WHATSAPP = "whatsapp",
  EMAIL = "email",
}

const ShareEstimate = (props: Props) => {
  const { customer, taskId, estimateShared, setEstimateShared } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { room } = useVideoContext();
  const { shareEstimate } = useMessages(room?.localParticipant);

  const [shareType, setShareType] = useState<string>(ShareType.APP);

  const [emailSended, setEmailSended] = useState<boolean>(false);

  const [email, setEmail] = useState<string>(customer?.email || "");

  const { mutate: sendEmail, isLoading: loadingSendEmail } = useMutation(
    ["mail.send"],
    (mail: Mail) => MailService.send(mail),
    {
      onSuccess: () => {
        NotificationToast.success(translate("emailSentSuccesfuly"));
      },
      onError: () => {
        NotificationToast.error(translate("couldNotSendEmail"));
      },
    }
  );

  const handleSendEmail = () => {
    if (!currentUser) return;

    const emailToSend: Mail = {
      isHTML: true,
      fromName: `${currentUser.name ? currentUser.name : ""} ${
        currentUser.lastName ? currentUser.lastName : ""
      }`,
      toEmail: email,
      message: `<div style="padding: 10px;">  <a href="https://shipeezi.com" target="_blank">
        <img src="https://shipeezi-fe-img-assets.s3.ap-southeast-2.amazonaws.com/sd.png" alt="Powered by Shipeezi" style="height: 26px" /></a>
        <hr />
        <br><p>Powered by <a href="https://shipeezi.com" target="_blank">Shipeezi</a></p><hr /><p>Shipeezi, 300 Barangaroo Ave, Sydney NSW 2000, Australia</p></div>`,
      subject: `Inventory from ${taskId} survey`,
      bcc: "",
      cc: "",
    };

    sendEmail(emailToSend);
    setEmailSended(true);
  };

  const handleShareEstimate = () => {
    const SHARE_TIMEOUT = 10000;

    setEstimateShared(true);
    shareEstimate();

    setTimeout(() => {
      setEstimateShared(false);
    }, SHARE_TIMEOUT);
  };

  const renderEmailComponent = () => (
    <Grid container spacing={2}>
      <Grid item md={12} lg={12}>
        <Input
          name="emailLink"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          placeholder={translate("contactEmailCom")}
        />
      </Grid>
      <Grid item container md={12} lg={12} justifyContent="flex-end">
        <Button
          // disabled={email ? email.length === 0 : true}
          disabled={true}
          onClick={handleSendEmail}
          customColor={Color.SEND}
          className={classes.buttonShare}
          loading={loadingSendEmail}
        >
          {emailSended ? (
            <FaCheck size={14} color="#465869" className="mr-2" />
          ) : (
            <FiSend size={14} color="#465869" className="mr-2" />
          )}
          {emailSended ? translate("sent") : translate("send")}
        </Button>
      </Grid>
    </Grid>
  );

  const renderShareEstimateComponent = () => (
    <Grid container spacing={2}>
      <Grid item container md={12} lg={12} justifyContent="flex-start">
        <Button
          customColor={Color.SEND}
          loading={loadingSendEmail}
          onClick={handleShareEstimate}
          // disabled={estimateShared}
          disabled={true}
        >
          <RiShareLine size={16} className="mr-2" />
          {estimateShared ? translate("shared") : translate("shareEstimate")}
        </Button>
      </Grid>
    </Grid>
  );

  const renderShareComponent = () => {
    switch (shareType) {
      case ShareType.APP:
        return renderShareEstimateComponent();
      case ShareType.EMAIL:
        return renderEmailComponent();
      default:
        return renderShareEstimateComponent();
    }
  };

  const setActiveItem = (value: string): string => {
    if (value === shareType) return "active";

    return "";
  };

  return (
    <section className="w-100">
      <h3 className={classes.title}>{translate("shareTheEstimateWithTheCustomer")}</h3>

      <ul className={classes.shareTypeList}>
        <li className={setActiveItem(ShareType.APP)} onClick={() => setShareType(ShareType.APP)}>
          {translate("shareViaApp")}
        </li>
        <li
          className={setActiveItem(ShareType.EMAIL)}
          onClick={() => setShareType(ShareType.EMAIL)}
        >
          {translate("shareViaEMail")}
        </li>
      </ul>

      {renderShareComponent()}
    </section>
  );
};

export default ShareEstimate;
